import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import { connectStateResults, connectAutoComplete } from 'react-instantsearch/connectors'
import { Component, h } from 'preact'
import algoliasearch from 'algoliasearch/lite'

import globals from '../../../utils/globals'
import { SEARCH_TOKEN_URL } from '../../../libs/config'
import getWithInMemoryCache from '../../../utils/getWithInMemoryCache'
import AlgoliaSearch from './AlgoliaSearch'
import AlgoliaSearchForControl from './AlgoliaSearchForControl'
import { Hit } from 'js/src/preact-components/algolia/components'
import { SearchBox } from './SearchBox'

const SearchWithAutoComplete = connectAutoComplete(connectStateResults(AlgoliaSearch))
const SearchWithAutoCompleteForControl = connectAutoComplete(connectStateResults(AlgoliaSearchForControl))

declare global {
    interface Window {
      aa: (command: string, data: {
        applicationID: string,
        apiKey: string
      }) => void
    }
}

interface Props {
  isControl?: boolean,
  isFederatedSearch?: boolean,
  isUserGuest?: boolean,
  searchPlaceholder?: string,
  callback?: (hit: Hit) => void,
  exclude?: Array<number>
  submitIconType?: string
}

interface State {
  searching: boolean,
  searchById: boolean,
  finalInput: string,
  clientId: string,
  apiKey: string,
}

class Search extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      searching: false,
      searchById: false,
      finalInput: '',
      clientId: '',
      apiKey: ''
    }
    this.updateSearch = this.updateSearch.bind(this)
  }

  updateSearch (value: string, next: () => void) {
    const maybeId = parseInt(value, 10)
    this.setState({
      searching: value !== '',
      searchById: !isNaN(maybeId),
      finalInput: value
    }, next)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})

    this.setState({
      clientId: data['client_id'],
      apiKey: data.token
    }, () => {
      if (typeof window.aa === 'function') {
        window.aa('init', {
          applicationID: this.state.clientId,
          apiKey: this.state.apiKey
        })
      }
    })
  }

  render (props: Props, state: State) {
    if (!state.clientId) {
      return <SearchBox
        submitIconType={props.submitIconType || ''}
      />
    }
    const communityId = globals('communityId')
    const searchClient = algoliasearch(state.clientId, state.apiKey)
    return <PureSearch
      searchClient={searchClient}
      updateSearch={this.updateSearch}
      searchById={state.searchById}
      finalInput={state.finalInput}
      searching={state.searching}
      communityId={typeof communityId === 'string' ? communityId : ''}
      submitIconType={props.submitIconType || ''}
      {...props} />
  }
}

interface PureProps extends Props {
  communityId: string,
  searching: boolean,
  searchById: boolean,
  finalInput: string,
  searchClient: algoliasearch,
  updateSearch: (value: string, cb: () => void) => void,
}

const PureSearch = (props: PureProps) => <InstantSearch
  searchClient={props.searchClient}
  indexName={props.communityId}
>
  <Index indexName={props.communityId}>
    <Configure
      hitsPerPage={5}
      clickAnalytics={props.searching}
      analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
      restrictSearchableAttributes={props.searchById ? ['id'] : ['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']} />
  </Index>

  {
    props.isFederatedSearch ? (
      <Index indexName={`${props.communityId}-zendesk`}>
        <Configure
          hitsPerPage={3}
          clickAnalytics={props.searching} />
      </Index>
    ) : null
  }

  {
    props.isControl ? (
      <SearchWithAutoCompleteForControl
        updateSearch={(value: string, next: () => void) => props.updateSearch(value, next)}
        searchById={props.searchById}
        finalInput={props.finalInput}
        placeholder={props.searchPlaceholder}
        callback={props.callback}
        exclude={props.exclude}
        submitIconType={props.submitIconType || ''}
      />
    ) : (
      <SearchWithAutoComplete
        updateSearch={(value: string, next: () => void) => props.updateSearch(value, next)}
        callback={props.callback}
        indexName={props.communityId}
        isFederatedSearch={props.isFederatedSearch}
      />
    )
  }
</InstantSearch>

export { PureSearch }
export default Search
